import React from 'react';
import { Trash2, Plus } from 'lucide-react';
import Input from './Input';
import Select from './Select';
import Button from './Button';
import FormField from './FormField';
import Card from './Card';
import Checkbox from './Checkbox';
import { maskCep, fetchCepData, unmask } from '../../utils/maskHelpers';

// Endereços inline reutilizáveis no cadastro de usuários (admin + supplier).
// Padrão: array com pelo menos 1 endereço, label entrega/cobrança/principal,
// integração com ViaCEP via fetchCepData. O componente é controlado — pai
// é dono do array.
//
// Forma de cada endereço (espelha addresses validator do backend):
//   { label, recipientName, zip, street, number, complement, district,
//     city, state, country, isDefault }
//
// O pai deve enviar o array assim no payload do POST /api/admin/users (ou
// /api/store-users) — backend cria os Address rows via Address.bulkCreate.

const LABEL_OPTIONS = [
  { value: 'principal', label: 'Principal' },
  { value: 'entrega', label: 'Entrega' },
  { value: 'cobranca', label: 'Cobrança' }
];

const STATE_OPTIONS = [
  '', 'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
].map((uf) => ({ value: uf, label: uf || '—' }));

export const emptyAddress = () => ({
  label: 'principal',
  recipientName: '',
  zip: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  country: 'BR',
  isDefault: false
});

const AddressFieldsArray = ({ addresses = [], onChange, required = false, minItems = 1 }) => {
  const update = (index, patch) => {
    const next = addresses.map((a, i) => (i === index ? { ...a, ...patch } : a));
    onChange(next);
  };

  const setDefault = (index) => {
    const next = addresses.map((a, i) => ({ ...a, isDefault: i === index }));
    onChange(next);
  };

  const handleZipChange = async (index, raw) => {
    const masked = maskCep(raw);
    update(index, { zip: masked });
    if (unmask(masked).length === 8) {
      const data = await fetchCepData(masked);
      if (data) {
        update(index, {
          zip: masked,
          street: data.logradouro || addresses[index]?.street || '',
          district: data.bairro || addresses[index]?.district || '',
          city: data.localidade || addresses[index]?.city || '',
          state: data.uf || addresses[index]?.state || ''
        });
      }
    }
  };

  const addAnother = () => {
    const next = [...addresses, emptyAddress()];
    // Se este for o primeiro, marca default automaticamente.
    if (next.length === 1) next[0].isDefault = true;
    onChange(next);
  };

  const remove = (index) => {
    if (addresses.length <= minItems) return;
    const removingDefault = !!addresses[index]?.isDefault;
    let next = addresses.filter((_, i) => i !== index);
    if (removingDefault && next.length > 0) next = next.map((a, i) => ({ ...a, isDefault: i === 0 }));
    onChange(next);
  };

  if (addresses.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-app-text-secondary">
          Nenhum endereço cadastrado.
        </p>
        <Button variant="outline" size="sm" leftIcon={<Plus size={14} />} onClick={addAnother}>
          Adicionar endereço
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {addresses.map((addr, index) => (
        <Card key={index} className="p-4 space-y-3 bg-app-surface">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
              <FormField label="Tipo">
                <Select
                  value={addr.label || 'principal'}
                  onChange={(e) => update(index, { label: e.target.value })}
                  options={LABEL_OPTIONS}
                />
              </FormField>
              <FormField label="CEP" required={required}>
                <Input
                  value={addr.zip}
                  onChange={(e) => handleZipChange(index, e.target.value)}
                  placeholder="00000-000"
                />
              </FormField>
              <FormField label="Destinatário">
                <Input
                  value={addr.recipientName || ''}
                  onChange={(e) => update(index, { recipientName: e.target.value })}
                  placeholder="Quem recebe"
                />
              </FormField>
            </div>
            {addresses.length > minItems && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Trash2 size={14} />}
                onClick={() => remove(index)}
                aria-label="Remover endereço"
              >
                Remover
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-4">
              <FormField label="Rua" required={required}>
                <Input
                  value={addr.street}
                  onChange={(e) => update(index, { street: e.target.value })}
                  placeholder="Rua, avenida..."
                />
              </FormField>
            </div>
            <div className="md:col-span-2">
              <FormField label="Número">
                <Input
                  value={addr.number || ''}
                  onChange={(e) => update(index, { number: e.target.value })}
                  placeholder="123"
                />
              </FormField>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FormField label="Bairro">
              <Input
                value={addr.district || ''}
                onChange={(e) => update(index, { district: e.target.value })}
              />
            </FormField>
            <FormField label="Complemento">
              <Input
                value={addr.complement || ''}
                onChange={(e) => update(index, { complement: e.target.value })}
              />
            </FormField>
            <FormField label="Cidade" required={required}>
              <Input
                value={addr.city}
                onChange={(e) => update(index, { city: e.target.value })}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FormField label="Estado" required={required}>
              <Select
                value={addr.state}
                onChange={(e) => update(index, { state: e.target.value.toUpperCase() })}
                options={STATE_OPTIONS}
              />
            </FormField>
            <FormField label="País">
              <Input
                value={addr.country || 'BR'}
                onChange={(e) => update(index, { country: e.target.value.toUpperCase().slice(0, 2) })}
              />
            </FormField>
            <div className="flex items-end pb-2">
              <Checkbox
                label="Endereço padrão"
                checked={!!addr.isDefault}
                onChange={() => setDefault(index)}
              />
            </div>
          </div>
        </Card>
      ))}

      <Button variant="outline" size="sm" leftIcon={<Plus size={14} />} onClick={addAnother}>
        Adicionar outro endereço
      </Button>
    </div>
  );
};

// Helper: prepara o array pra envio ao backend (CEP só dígitos, label canonizado).
export function serializeAddressesForApi(addresses) {
  return (addresses || []).map((a) => ({
    label: a.label || 'principal',
    recipientName: a.recipientName || null,
    zip: unmask(a.zip),
    street: a.street,
    number: a.number || null,
    complement: a.complement || null,
    district: a.district || null,
    city: a.city,
    state: a.state,
    country: a.country || 'BR',
    isDefault: !!a.isDefault
  }));
}

export default AddressFieldsArray;
